import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Header, BottomNav } from "../components/Header";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { ArrowLeft, MessageCircle, Package, ShoppingCart, Gavel, Camera, Phone, CreditCard, CheckCircle, Loader2, Star, Quote, Bell, Edit2 } from "lucide-react";
import NotificationSettings from "../components/NotificationSettings";

const ProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const fileInputRef = useRef(null);
  const [profile, setProfile] = useState(null);
  const [products, setProducts] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [sellerReviews, setSellerReviews] = useState([]);
  const [sellerRating, setSellerRating] = useState({ average: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("active");
  const [uploading, setUploading] = useState(false);
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [whatsappDialogOpen, setWhatsappDialogOpen] = useState(false);
  const [whatsappValue, setWhatsappValue] = useState("");
  const [savingWhatsapp, setSavingWhatsapp] = useState(false);

  const isOwnProfile = user?.user_id === userId;
  const isAdmin = profile?.is_admin || false;

  useEffect(() => {
    fetchProfile();
    fetchSellerReviews();
  }, [userId]);

  useEffect(() => {
    if (userId) {
      fetchProducts();
    }
  }, [userId]);

  // Fetch purchases more aggressively
  useEffect(() => {
    if (user && token && user.user_id === userId) {
      // Fetch immediately
      fetchPurchases();
      
      // Also set up polling every 5 seconds for reliability
      const interval = setInterval(() => {
        if (purchases.length === 0) {
          console.log("Retrying fetch purchases (empty list)...");
          fetchPurchases();
        }
      }, 5000);
      
      return () => clearInterval(interval);
    }
  }, [user, token, userId]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/users/${userId}`);
      setProfile(response.data);
    } catch (error) {
      toast.error("Usuario no encontrado");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`, { params: { user_id: userId, include_sold: true } });
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const fetchSellerReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/seller/${userId}`);
      setSellerReviews(response.data || []);
      
      // Calculate average rating
      if (response.data && response.data.length > 0) {
        const total = response.data.reduce((sum, r) => sum + r.rating, 0);
        setSellerRating({
          average: (total / response.data.length).toFixed(1),
          count: response.data.length
        });
      }
    } catch (error) {
      console.error("Error fetching seller reviews:", error);
    }
  };

  const fetchPurchases = async () => {
    if (!token) return;
    try {
      console.log("Fetching purchases for user:", userId);
      const response = await axios.get(`${API}/my/purchases?t=${Date.now()}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      console.log("Purchases response:", response.data);
      setPurchases(response.data || []);
    } catch (error) {
      console.error("Error fetching purchases:", error);
    }
  };

  const handleMessage = () => {
    if (!user) {
      toast.error("Inicia sesión para enviar mensaje");
      navigate("/login");
      return;
    }
    navigate(`/chat/${userId}`);
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Solo se permiten imágenes JPG, PNG o WebP");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error("La imagen no puede superar 2MB");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const uploadResponse = await axios.post(`${API}/upload/image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        },
        withCredentials: true
      });

      // ImgBB returns the full URL directly
      const newPictureUrl = uploadResponse.data.url;

      // Update profile picture
      await axios.put(`${API}/auth/update-picture`, { picture: newPictureUrl }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      setProfile(prev => ({ ...prev, picture: newPictureUrl }));
      setPhotoDialogOpen(false);
      toast.success("Foto de perfil actualizada");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al subir imagen");
    } finally {
      setUploading(false);
    }
  };

  const handleSaveWhatsapp = async () => {
    if (!whatsappValue.trim() || whatsappValue.length < 10) {
      toast.error("Ingresa un número de WhatsApp válido");
      return;
    }
    setSavingWhatsapp(true);
    try {
      await axios.post(`${API}/auth/update-whatsapp`, 
        { whatsapp: whatsappValue.trim() },
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      setProfile(prev => ({ ...prev, whatsapp: whatsappValue.trim() }));
      setWhatsappDialogOpen(false);
      toast.success("WhatsApp actualizado");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar WhatsApp");
    } finally {
      setSavingWhatsapp(false);
    }
  };

  const openWhatsappDialog = () => {
    setWhatsappValue(profile?.whatsapp || "+507 ");
    setWhatsappDialogOpen(true);
  };

  // Filter products by status
  const activeProducts = products.filter(p => !p.is_sold);
  const soldProducts = products.filter(p => p.is_sold);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <Header />
        <div className="flex justify-center py-20">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (!profile) return null;

  return (
    <div className="min-h-screen bg-[#FAFAF9] pb-20 md:pb-8">
      <Header />

      <main className="max-w-2xl mx-auto px-4 py-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#64748B] hover:text-[#0F172A] mb-6"
          data-testid="back-button"
        >
          <ArrowLeft size={20} />
          Volver
        </button>

        {/* Profile Header */}
        <div className="bg-white rounded-2xl p-6 border border-[#E2E8F0] mb-6" data-testid="profile-header">
          <div className="flex items-start gap-6">
            <div className="relative">
              <Avatar className="h-24 w-24">
                <AvatarImage src={profile.picture} alt={profile.name} />
                <AvatarFallback className="bg-[#475569] text-white text-3xl">
                  {profile.name?.charAt(0)?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              {isOwnProfile && (
                <button
                  onClick={() => setPhotoDialogOpen(true)}
                  className="absolute -bottom-1 -right-1 bg-[#475569] text-white p-2 rounded-full hover:bg-[#334155] transition-colors"
                  data-testid="change-photo-btn"
                >
                  <Camera size={14} />
                </button>
              )}
            </div>

            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="text-2xl font-bold text-[#0F172A]" data-testid="profile-name">{profile.name}</h1>
                    {profile.sold_count > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold" data-testid="profile-sold-badge">
                        <CheckCircle size={12} />
                        {profile.sold_count} {profile.sold_count === 1 ? "vendido" : "vendidos"}
                      </span>
                    )}
                  </div>
                  {isAdmin && (
                    <Badge className="mt-1 bg-[#475569]">Administrador</Badge>
                  )}
                </div>
                {!isOwnProfile && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full"
                    onClick={handleMessage}
                    data-testid="message-user"
                  >
                    <MessageCircle size={16} className="mr-2" />
                    Mensaje
                  </Button>
                )}
              </div>

              {/* WhatsApp - Editable for own profile */}
              {isOwnProfile && (
                <div className="mt-4">
                  <button
                    onClick={openWhatsappDialog}
                    className="flex items-center gap-2 text-sm text-[#64748B] hover:text-[#0F172A] transition-colors p-2 -ml-2 rounded-lg hover:bg-[#F1F5F9]"
                    data-testid="edit-whatsapp-btn"
                  >
                    <Phone size={16} className="text-[#34D399]" />
                    <span>
                      {profile.whatsapp ? profile.whatsapp : "Agregar WhatsApp"}
                    </span>
                    <Edit2 size={14} className="text-[#94A3B8]" />
                  </button>
                </div>
              )}

              {/* Stats - Enhanced for Admin */}
              {isAdmin && (
                <div className="flex flex-wrap items-center gap-4 sm:gap-6 mt-4">
                  <div className="text-center">
                    <p className="text-xl font-bold text-[#0F172A]">{activeProducts.length}</p>
                    <p className="text-sm text-[#64748B]">Activos</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold text-[#10B981]">{soldProducts.length}</p>
                    <p className="text-sm text-[#64748B]">Vendidos</p>
                  </div>
                  {sellerRating.count > 0 && (
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Star size={18} className="text-[#F59E0B] fill-[#F59E0B]" />
                        <span className="text-xl font-bold text-[#F59E0B]">{sellerRating.average}</span>
                      </div>
                      <p className="text-sm text-[#64748B]">{sellerRating.count} reseñas</p>
                    </div>
                  )}
                </div>
              )}

              {!isAdmin && isOwnProfile && (
                <div className="grid grid-cols-3 gap-2 sm:flex sm:items-center sm:gap-6 mt-4">
                  <div className="text-center">
                    <p className="text-lg sm:text-xl font-bold text-[#0F172A]">{purchases.length}</p>
                    <p className="text-xs sm:text-sm text-[#64748B]">Compras</p>
                  </div>
                  {profile.buyer_rating > 0 && (
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <span className="text-lg sm:text-xl font-bold text-[#F59E0B]">{profile.buyer_rating}</span>
                        <span className="text-[#F59E0B]">★</span>
                      </div>
                      <p className="text-xs sm:text-sm text-[#64748B]">Calificación</p>
                    </div>
                  )}
                  <div className="text-center">
                    <p className="text-lg sm:text-xl font-bold text-[#10B981]">{profile.purchases_completed || 0}</p>
                    <p className="text-xs sm:text-sm text-[#64748B]">Completadas</p>
                  </div>
                  {(profile.purchases_cancelled || 0) > 0 && (
                    <div className="text-center col-span-full sm:col-span-1">
                      <p className="text-lg sm:text-xl font-bold text-[#EF4444]">{profile.purchases_cancelled}</p>
                      <p className="text-xs sm:text-sm text-[#64748B]">Canceladas</p>
                    </div>
                  )}
                </div>
              )}

              {/* Buyer rating for viewing other profiles */}
              {!isAdmin && !isOwnProfile && profile.buyer_rating > 0 && (
                <div className="flex items-center gap-4 mt-4 p-3 bg-[#FEF3C7] rounded-lg">
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <span
                        key={star}
                        className={star <= Math.round(profile.buyer_rating) ? "text-[#F59E0B]" : "text-[#E2E8F0]"}
                      >
                        ★
                      </span>
                    ))}
                  </div>
                  <span className="text-sm text-[#92400E]">
                    {profile.buyer_rating} ({profile.buyer_rating_count} reseñas)
                  </span>
                </div>
              )}

              {profile.created_at && (
                <p className="text-sm text-[#64748B] mt-4">
                  Miembro desde {formatDistanceToNow(new Date(profile.created_at), { addSuffix: true, locale: es })}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Notification Settings - Only for own profile */}
        {isOwnProfile && (
          <div className="mb-6">
            <NotificationSettings token={token} />
          </div>
        )}

        {/* Content based on user type */}
        {isAdmin ? (
          /* Admin sees their products and reviews */
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full bg-[#F1F5F9] rounded-full p-1 mb-6 grid grid-cols-3">
              <TabsTrigger 
                value="active" 
                className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm"
                data-testid="tab-active"
              >
                <Package size={14} className="mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Activos</span> ({activeProducts.length})
              </TabsTrigger>
              <TabsTrigger 
                value="sold" 
                className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm"
                data-testid="tab-sold"
              >
                <ShoppingCart size={14} className="mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Vendidos</span> ({soldProducts.length})
              </TabsTrigger>
              <TabsTrigger 
                value="reviews" 
                className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm"
                data-testid="tab-reviews"
              >
                <Star size={14} className="mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Reseñas</span> ({sellerReviews.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="active" className="mt-0">
              {activeProducts.length === 0 ? (
                <div className="empty-state" data-testid="empty-products">
                  <Package size={64} />
                  <h3 className="text-lg font-semibold text-[#0F172A] mt-4">No hay productos activos</h3>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4" data-testid="products-grid">
                  {activeProducts.map((product) => (
                    <Link
                      key={product.product_id}
                      to={`/product/${product.product_id}`}
                      className="bg-white rounded-xl overflow-hidden border border-[#E2E8F0] hover:shadow-lg transition-shadow"
                    >
                      <div className="aspect-square overflow-hidden bg-[#F1F5F9]">
                        <img 
                          src={product.image_url} 
                          alt={product.title} 
                          className="w-full h-full object-cover hover:scale-105 transition-transform"
                        />
                      </div>
                      <div className="p-3">
                        <div className="flex items-center gap-1 mb-1">
                          {product.sale_type === "auction" ? (
                            <span className="text-xs bg-[#0F172A] text-white px-2 py-0.5 rounded-full flex items-center gap-1">
                              <Gavel size={10} /> Subasta
                            </span>
                          ) : (
                            <span className="text-xs bg-[#475569] text-white px-2 py-0.5 rounded-full flex items-center gap-1">
                              <ShoppingCart size={10} /> Venta
                            </span>
                          )}
                        </div>
                        <h3 className="font-semibold text-sm text-[#0F172A] line-clamp-1">{product.title}</h3>
                        <p className="text-lg font-bold text-[#10B981] mt-1">
                          ${(product.current_bid || product.price)?.toLocaleString()}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="sold" className="mt-0">
              {soldProducts.length === 0 ? (
                <div className="empty-state" data-testid="empty-sold">
                  <ShoppingCart size={64} />
                  <h3 className="text-lg font-semibold text-[#0F172A] mt-4">No hay productos vendidos</h3>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4" data-testid="sold-grid">
                  {soldProducts.map((product) => (
                    <Link
                      key={product.product_id}
                      to={`/product/${product.product_id}`}
                      className="bg-white rounded-xl overflow-hidden border border-[#E2E8F0] hover:shadow-lg transition-shadow relative"
                    >
                      <div className="absolute top-0 left-0 right-0 bg-[#3B82F6] text-white text-xs font-bold py-1 text-center z-10">
                        Vendido
                      </div>
                      <div className="aspect-square overflow-hidden bg-[#F1F5F9] mt-6">
                        <img 
                          src={product.image_url} 
                          alt={product.title} 
                          className="w-full h-full object-cover opacity-80"
                        />
                      </div>
                      <div className="p-3">
                        <h3 className="font-semibold text-sm text-[#0F172A] line-clamp-1">{product.title}</h3>
                        <p className="text-lg font-bold text-[#10B981] mt-1">
                          ${(product.final_price || product.price)?.toLocaleString()}
                        </p>
                        {product.buyer_name && (
                          <p className="text-xs text-[#64748B] mt-1">
                            Comprador: {product.buyer_name}
                          </p>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="reviews" className="mt-0">
              {sellerReviews.length === 0 ? (
                <div className="empty-state" data-testid="empty-reviews">
                  <Star size={64} className="text-[#E2E8F0]" />
                  <h3 className="text-lg font-semibold text-[#0F172A] mt-4">No hay reseñas todavía</h3>
                  <p className="text-sm text-[#64748B]">Las reseñas de compradores aparecerán aquí</p>
                </div>
              ) : (
                <div className="space-y-4" data-testid="reviews-list">
                  {/* Rating Summary */}
                  <div className="bg-gradient-to-r from-[#F59E0B]/10 to-[#FEF3C7] rounded-2xl p-6 text-center mb-6">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star 
                          key={star} 
                          size={28} 
                          className={star <= Math.round(parseFloat(sellerRating.average)) ? "text-[#F59E0B] fill-[#F59E0B]" : "text-[#E2E8F0]"} 
                        />
                      ))}
                    </div>
                    <p className="text-3xl font-bold text-[#0F172A]">{sellerRating.average}</p>
                    <p className="text-sm text-[#64748B]">Basado en {sellerRating.count} reseñas</p>
                  </div>
                  
                  {/* Reviews List */}
                  {sellerReviews.map((review) => (
                    <Card key={review.review_id} className="border-[#E2E8F0]">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={review.reviewer_picture} />
                            <AvatarFallback className="bg-[#475569] text-white">
                              {review.reviewer_name?.charAt(0)?.toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <h4 className="font-semibold text-[#0F172A]">{review.reviewer_name}</h4>
                              <div className="flex items-center gap-1">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <Star 
                                    key={star} 
                                    size={14} 
                                    className={star <= review.rating ? "text-[#F59E0B] fill-[#F59E0B]" : "text-[#E2E8F0]"} 
                                  />
                                ))}
                              </div>
                            </div>
                            <p className="text-xs text-[#64748B] mb-2">
                              {review.product_title} • {formatDistanceToNow(new Date(review.created_at), { addSuffix: true, locale: es })}
                            </p>
                            {review.comment && (
                              <div className="bg-[#F8FAFC] rounded-lg p-3 mt-2">
                                <Quote size={14} className="text-[#94A3B8] mb-1" />
                                <p className="text-sm text-[#64748B] italic">{review.comment}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        ) : isOwnProfile ? (
          /* Regular user sees their purchases */
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-[#0F172A] flex items-center gap-2">
              <ShoppingCart size={20} />
              Mis Compras
            </h2>
            
            {purchases.length === 0 ? (
              <div className="empty-state" data-testid="empty-purchases">
                <ShoppingCart size={64} />
                <h3 className="text-lg font-semibold text-[#0F172A] mt-4">No tienes compras aún</h3>
                <p className="text-[#64748B] mt-2">Explora los productos disponibles</p>
                <Link to="/">
                  <Button className="mt-4 rounded-full bg-[#475569] hover:bg-[#334155]">
                    Ver productos
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {purchases.map((purchase) => (
                  <Card key={purchase.product_id} className="border-[#E2E8F0]">
                    <CardContent className="p-4">
                      <Link to={`/product/${purchase.product_id}`} className="flex gap-4">
                        <img
                          src={purchase.image_url}
                          alt={purchase.title}
                          className="w-20 h-20 rounded-xl object-cover"
                        />
                        <div className="flex-1">
                          <h3 className="font-semibold text-[#0F172A] line-clamp-1">{purchase.title}</h3>
                          <p className="text-lg font-bold text-[#10B981]">
                            ${(purchase.final_price || purchase.price)?.toLocaleString()}
                          </p>
                          <Badge variant="outline" className="mt-2 text-xs">
                            {purchase.sale_type === "auction" ? "Subasta ganada" : "Compra directa"}
                          </Badge>
                        </div>
                      </Link>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* Viewing another user's profile - show message option only */
          <div className="empty-state" data-testid="other-profile">
            <MessageCircle size={64} />
            <h3 className="text-lg font-semibold text-[#0F172A] mt-4">Perfil de {profile.name}</h3>
            <Button onClick={handleMessage} className="mt-4 rounded-full bg-[#475569] hover:bg-[#334155]">
              <MessageCircle size={16} className="mr-2" />
              Enviar mensaje
            </Button>
          </div>
        )}
      </main>

      {/* Change Photo Dialog */}
      <Dialog open={photoDialogOpen} onOpenChange={setPhotoDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cambiar foto de perfil</DialogTitle>
            <DialogDescription>
              Sube una nueva imagen para tu perfil (max 2MB)
            </DialogDescription>
          </DialogHeader>
          
          <input
            type="file"
            ref={fileInputRef}
            onChange={handlePhotoUpload}
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
          />
          
          <div className="flex flex-col items-center py-6">
            <Avatar className="h-32 w-32 mb-4">
              <AvatarImage src={profile.picture} alt={profile.name} />
              <AvatarFallback className="bg-[#475569] text-white text-4xl">
                {profile.name?.charAt(0)?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="rounded-full bg-[#475569] hover:bg-[#334155]"
              data-testid="upload-photo-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Subiendo...
                </>
              ) : (
                <>
                  <Camera className="mr-2 h-4 w-4" />
                  Seleccionar imagen
                </>
              )}
            </Button>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPhotoDialogOpen(false)} className="rounded-full">
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Edit Dialog */}
      <Dialog open={whatsappDialogOpen} onOpenChange={setWhatsappDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="h-5 w-5 text-[#34D399]" />
              WhatsApp
            </DialogTitle>
            <DialogDescription>
              Te contactaremos por WhatsApp para coordinar entregas de productos
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Input
              value={whatsappValue}
              onChange={(e) => setWhatsappValue(e.target.value)}
              placeholder="+507 6123 4567"
              className="rounded-xl text-lg"
              data-testid="whatsapp-input"
            />
            <p className="text-xs text-[#64748B] mt-2">
              Incluye el código de país (ej: +507 para Panamá)
            </p>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setWhatsappDialogOpen(false)} className="rounded-full">
              Cancelar
            </Button>
            <Button 
              onClick={handleSaveWhatsapp}
              disabled={savingWhatsapp || whatsappValue.length < 10}
              className="rounded-full bg-[#34D399] hover:bg-[#10B981]"
              data-testid="save-whatsapp-btn"
            >
              {savingWhatsapp ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Guardando...
                </>
              ) : (
                "Guardar"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  );
};

export default ProfilePage;
